import React from 'react';
import { formatDate, formatDateWithTime } from 'Common/HelperClass';
import Button from 'Components/DesignSystem/Button/Button';
import Status from 'Components/DesignSystem/Status/Status';
import Tooltip from 'Components/DesignSystem/Tooltip/Tooltip';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { AlertTriangle, Delete } from 'Components/DesignSystem/Iconography/Icons';
import { StatusTone } from 'types/DesignSystem/Status';
import { TableColumn } from 'types/DesignSystem/Table';

/**
 * The connector log grid's columns, as DS `Table` definitions.
 *
 * `Constants.js` stays the source of truth for WHICH columns each tab shows and what
 * they read — that is business configuration, pinned by `Constants.test.js`, and it is
 * untouched. This file is the adapter: it turns one legacy `{ label, map, options }`
 * header into one `TableColumn`, reproducing every per-label special case the old
 * `Components/Common/Table/Table.jsx` had baked into its render loop.
 *
 * Keeping the two apart matters because the legacy table dispatched on the **display
 * label** — `label === 'Status'`, `label === 'View Logs'`, `label.toLowerCase() ===
 * 'delete'`. That is a fragile contract (renaming a column silently changes its
 * behaviour), but it is the shipped one, and reproducing it exactly is the difference
 * between a migration and a rewrite. The fragility is now at least confined to one
 * documented function instead of being spread through a 216-line render.
 */

export type LegacyHeader = {
  label: string;
  map?: string | null;
  options?: {
    sort?: boolean;
    copy?: boolean;
    truncate?: number;
    isLink?: boolean;
    index?: boolean;
    api?: string;
    apiKey?: string;
    callback?: (id: string, refresh: () => void) => void | Promise<void>;
    handleSwitch?: (event: unknown, row: Record<string, unknown>) => void;
  };
};

export type ConnectionRow = Record<string, any>;

export type ColumnHandlers = {
  /**
   * The row is passed alongside its index, the way `onDelete` already receives it.
   *
   * Not cosmetic: `ConnectionsHome` memoises its columns on `[headers]`, so the handler
   * captured here is the one from the FIRST render and closes over that render's `data`
   * — which is empty. Anything the handler tries to look up BY INDEX in `data` therefore
   * reads undefined forever. Passing the row removes the lookup, and with it the
   * dependency on a closure that is stale by construction.
   */
  onViewLog: (rowIndex: number, row: ConnectionRow) => void;
  onDelete: (row: ConnectionRow, header: LegacyHeader) => void;
  /** Prefix for every generated testid, so two grids on one page cannot collide. */
  baseId: string;
};

/**
 * The legacy timestamp heuristic, ported verbatim.
 *
 * The connector service sends `created_at` sometimes in seconds and sometimes in
 * milliseconds, and this counts the digits before the decimal point to decide: 13
 * digits is already milliseconds, anything else gets multiplied. It is a guess, and it
 * is the guess the screen has always made — changing it would move every date in the
 * table, which is not a design-system concern.
 */
export const multiplyIfSeconds = (value: number): number => {
  const asString = value.toString();
  const decimalAt = asString.indexOf('.');
  const digitsBeforeDecimal = decimalAt === -1 ? asString.length : decimalAt;
  return digitsBeforeDecimal === 13 ? value : value * 1000;
};

const DATE_ONLY_LABELS = ['Started At', 'Created At'];
const DATE_TIME_LABELS = ['Started Date', 'End Date', 'Created', 'Created on', 'CREATED ON'];

/**
 * Job status → chip tone, **with a fallback**.
 *
 * The fallback is the point. `Components/GMS/Rules/RulesQueue.jsx` carried the same
 * kind of map read as `colorObj[status][0]` with no default, and any status outside
 * its four known strings threw *during render* and took down the whole drawer
 * (§ 109). These strings come from `midway_connector_svc`; one new enum value on the
 * server must degrade to a neutral chip showing its own text, not to a blank screen.
 *
 * `partially_valid` is deliberately NOT folded in here. A job that completed with some
 * records rejected is still a completed job, and recolouring it danger would say the
 * import failed when it did not. It keeps its success chip and gains the warning
 * triangle beside it — which is exactly what the legacy row showed, minus the chip.
 */
export const statusTone = (status: unknown): StatusTone => {
  const normalised = String(status ?? '').trim().toLowerCase();
  if (normalised === 'completed' || normalised === 'success' || normalised === 'done') return 'success';
  if (normalised.includes('error') || normalised === 'failed' || normalised === 'failure') return 'danger';
  if (normalised === 'in progress' || normalised === 'processing' || normalised === 'running') return 'info';
  return 'neutral';
};

const partialFailureMessage = (row: ConnectionRow): string =>
  `Completed: ${row.process_metrics?.completed_percentage}%\n` +
  `Total Records Processed: ${row.process_metrics?.total_records_processed}\n` +
  `Products Failed: ${row.process_metrics?.records_failed}`;

/** One legacy header → one DS column. Exported for the unit tests. */
export const toDsColumn = (
  header: LegacyHeader,
  index: number,
  handlers: ColumnHandlers,
): TableColumn<ConnectionRow> => {
  const { label, map, options } = header;
  const { baseId } = handlers;
  /* `map` is null on the action columns ("View Logs", "Delete"), so it cannot be the
     id. The label is unique within a header set, and the index disambiguates anyway. */
  const id = map || `${label.toLowerCase().replace(/\s+/g, '-')}-${index}`;
  const base = {
    id,
    header: label,
    accessor: (map ?? '') as string,
    sortable: !!options?.sort,
  };

  if (DATE_ONLY_LABELS.includes(label)) {
    return {
      ...base,
      cellType: 'meta',
      render: (_value, row) => (map && row[map] ? formatDate(multiplyIfSeconds(row[map])) : ''),
    };
  }

  if (DATE_TIME_LABELS.includes(label)) {
    return {
      ...base,
      cellType: 'meta',
      render: (_value, row) =>
        map && row[map] ? formatDateWithTime(multiplyIfSeconds(row[map])) : '',
    };
  }

  if (label === 'View Logs') {
    return {
      ...base,
      /* Keyed on the row's uid, not `rowIndex`, like `Status` and `partial` a few lines
         down — and unlike the index form this used to carry. This screen re-fetches
         itself every 30s (release-sept-29), so a row's POSITION is the one thing about
         it that is not stable: a spec pinned to `-view-logs-3` silently starts driving
         a different connector's logs the moment a run is inserted or drops out. The
         repo rule in CLAUDE.md says the same thing in general terms. */
      render: (_value, row, rowIndex) => (
        <Button
          variant="tertiary"
          size="sm"
          data-testid={`${baseId}-view-logs-${row.uid ?? row.id ?? rowIndex}`}
          onClick={() => handlers.onViewLog(rowIndex, row)}
        >
          View Logs
        </Button>
      ),
    };
  }

  if (label === 'Status') {
    return {
      ...base,
      cellType: 'tag',
      render: (_value, row) => {
        const status = map ? row[map] : '';
        const partiallyFailed = status === 'Completed' && row.partially_valid;
        return (
          <div className="tw-flex tw-items-center tw-gap-2">
            <Status
              tone={statusTone(status)}
              label={String(status ?? '')}
              data-testid={`${baseId}-status-${row.uid ?? row.id ?? ''}`}
            />
            {partiallyFailed && row.process_metrics && (
              <Tooltip content={partialFailureMessage(row)} placement="top" usePortal>
                {/* The triangle is a real element with an accessible name, not the
                    bare `YellowWarningIcon` the legacy row drew. A wordless glyph
                    carrying the only signal that records were rejected fails
                    SC 1.1.1 — a screen reader heard "Completed" and nothing more. */}
                <span
                  role="img"
                  aria-label="Completed with rejected records"
                  className="tw-inline-flex tw-text-tone-warning-glyph"
                  data-testid={`${baseId}-partial-${row.uid ?? row.id ?? ''}`}
                >
                  <AlertTriangle size={16} aria-hidden="true" />
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    };
  }

  if (label.toLowerCase() === 'delete') {
    return {
      ...base,
      align: 'center',
      render: (_value, row, rowIndex) => (
        <Button
          variant="tertiary"
          size="sm"
          aria-label="Delete"
          data-testid={`${baseId}-delete-${row.uid ?? row.id ?? rowIndex}`}
          onClick={() => handlers.onDelete(row, header)}
          className="tw-text-tone-danger-ink"
        >
          <Delete />
        </Button>
      ),
    };
  }

  /*
    The default cell. `copy` and `isLink` are carried over from the legacy options bag
    even though no connector header sets either — `Constants.js` is shared shape with
    other screens' header sets, and dropping the branches here would make this adapter
    quietly lossy for the next caller.
  */
  return {
    ...base,
    render: (_value, row, rowIndex) => {
      if (!map) return '';
      const raw = row[map];
      if (options?.isLink) {
        return (
          <Button
            variant="tertiary"
            size="sm"
            data-testid={`${baseId}-link-${rowIndex}`}
            onClick={() => handlers.onViewLog(rowIndex, row)}
          >
            {raw}
          </Button>
        );
      }
      if (options?.truncate) {
        return (
          <Tooltip content={String(raw ?? '')} placement="top" usePortal>
            <span>{`${String(raw ?? '').slice(0, options.truncate)}...`}</span>
          </Tooltip>
        );
      }
      return (
        <Typography variant="body-primary" as="span">
          {raw}
        </Typography>
      );
    },
  };
};

/** The whole header set → DS columns. */
export const toDsColumns = (
  headers: LegacyHeader[] | undefined,
  handlers: ColumnHandlers,
): TableColumn<ConnectionRow>[] =>
  (headers ?? []).map((header, index) => toDsColumn(header, index, handlers));
